<script setup>
import { formatTimeAgo } from '@vueuse/core';
import { computed } from 'vue';
import PoolShareRateChip from '@/components/posts/PoolShareRateChip.vue';

const props = defineProps({
  supplier: {
    type: Object,
    required: true,
    validator: (supplier) => {
      return supplier && typeof supplier.name === 'string' && typeof supplier.username === 'string';
    },
  },
  timestamp: {
    type: String,
    required: true,
  },
  telegramUrl: {
    type: String,
    default: null,
  },
});

const emit = defineEmits(['click']);
const arabicMessages = {
  justNow: 'الآن',
  past: (n) => n,
  future: (n) => `بعد ${n}`,
  invalid: 'تاريخ غير صالح',

  second: (n) => {
    if (n === 1) return 'ثانية واحدة';
    if (n === 2) return 'ثانيتان';
    if (n >= 3 && n <= 10) return `${n} ثوانٍ`;
    return `${n} ثانية`;
  },

  minute: (n) => {
    if (n === 1) return 'دقيقة واحدة';
    if (n === 2) return 'دقيقتان';
    if (n >= 3 && n <= 10) return `${n} دقائق`;
    return `${n} دقيقة`;
  },

  hour: (n) => {
    if (n === 1) return 'ساعة واحدة';
    if (n === 2) return 'ساعتان';
    if (n >= 3 && n <= 10) return `${n} ساعات`;
    return `${n} ساعة`;
  },

  day: (n) => {
    if (n === 1) return 'يوم واحد';
    if (n === 2) return 'يومان';
    if (n >= 3 && n <= 10) return `${n} أيام`;
    return `${n} يومًا`;
  },

  week: (n) => {
    if (n === 1) return 'أسبوع واحد';
    if (n === 2) return 'أسبوعان';
    if (n >= 3 && n <= 10) return `${n} أسابيع`;
    return `${n} أسبوعًا`;
  },

  month: (n) => {
    if (n === 1) return 'شهر واحد';
    if (n === 2) return 'شهران';
    if (n >= 3 && n <= 10) return `${n} أشهر`;
    return `${n} شهرًا`;
  },

  year: (n) => {
    if (n === 1) return 'سنة واحدة';
    if (n === 2) return 'سنتان';
    if (n >= 3 && n <= 10) return `${n} سنوات`;
    return `${n} سنة`;
  },
};
const timeAgo = computed(() => {
  return formatTimeAgo(new Date(props.timestamp), { messages: arabicMessages });
});

const hasAvatarImage = computed(() => {
  return props.supplier.avatarUrl && props.supplier.avatarUrl.length > 0;
});

const handleHeaderClick = () => {
  emit('click');
};
</script>

<template>
  <f7-card-header class="post-header">
    <div class="header-content" @click="handleHeaderClick">
      <div v-if="hasAvatarImage" class="avatar-wrapper">
        <img
          :src="supplier.avatarUrl"
          :alt="supplier.name"
          class="avatar-image"
          loading="lazy"
          @error="(e) => (e.target.style.display = 'none')"
        />
      </div>

      <div v-else class="avatar-placeholder" :class="supplier.gradient">
        {{ supplier.avatar }}
      </div>

      <div class="supplier-info">
        <div class="display-flex align-items-center">
          <span class="supplier-name text-secondary">{{ supplier.name }}</span>
          <f7-icon
            v-if="supplier.verified"
            ios="f7:checkmark_seal_fill"
            md="material:verified"
            size="16"
            color="blue"
            class="margin-left-half"
          ></f7-icon>
        </div>
        <div class="post-meta-row">
          <span class="timestamp text-color-gray">{{ timeAgo }}</span>
          <!-- Per-channel PoolShare gain chip (post.channel.poolShareRate);
               renders nothing when the channel has no active program. -->
          <pool-share-rate-chip :rate="supplier.poolShareRate"></pool-share-rate-chip>
        </div>
      </div>
    </div>

    <f7-link
      v-if="telegramUrl"
      :href="telegramUrl"
      external
      icon-color="blue"
      target="_system"
      icon-f7="arrow_up_right_square"
      text="المصدر"
      icon-size="16px"
    >
    </f7-link>
  </f7-card-header>
</template>

<style scoped>
.post-header {
  padding: var(--poolit-spacing-md);
  border-bottom: 1px solid var(--f7-card-outline-border-color);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--poolit-spacing-sm);
}

.header-content {
  display: flex;
  align-items: center;
  gap: var(--poolit-spacing-sm);
  flex: 1;
  min-width: 0;
  cursor: pointer;
  transition: opacity var(--poolit-transition-fast);
}

.header-content:active {
  opacity: 0.7;
}

.avatar-wrapper,
.avatar-placeholder {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  flex-shrink: 0;
}

.avatar-wrapper {
  overflow: hidden;
  background-color: var(--f7-list-item-media-bg-color);
}

.avatar-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: var(--poolit-font-weight-semibold);
  font-size: 1rem;
}

/* Gradient classes using Poolit colors */
.avatar-placeholder.from-blue-500 {
  background: linear-gradient(135deg, var(--poolit-secondary-500), var(--poolit-secondary-600));
}

.avatar-placeholder.from-purple-500 {
  background: linear-gradient(135deg, var(--poolit-primary-500), var(--poolit-primary-600));
}

.avatar-placeholder.from-green-500 {
  background: linear-gradient(135deg, var(--poolit-success-500), var(--poolit-success-600));
}

.avatar-placeholder.from-red-500 {
  background: linear-gradient(135deg, var(--poolit-error-500), var(--poolit-error-600));
}

.avatar-placeholder.from-orange-500 {
  background: linear-gradient(135deg, var(--poolit-warning-500), var(--poolit-warning-600));
}

.avatar-placeholder.from-pink-500 {
  background: linear-gradient(135deg, #ec4899, #db2777);
}

.avatar-placeholder.from-indigo-500 {
  background: linear-gradient(135deg, #6366f1, #4f46e5);
}

.supplier-info {
  flex: 1;
  min-width: 0;
}

.supplier-name {
  font-size: var(--poolit-font-size-body);
  font-weight: var(--poolit-font-weight-semibold);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.post-meta-row {
  display: flex;
  align-items: center;
  gap: var(--poolit-spacing-sm);
  margin-top: 2px;
  flex-wrap: wrap;
}

.timestamp {
  font-size: var(--poolit-font-size-small);
}

.source-link {
  flex-shrink: 0;
  padding: 6px 12px;
  background: linear-gradient(135deg, #2aabee, #229ed9);
  border-radius: var(--poolit-radius-full);
  font-size: var(--poolit-font-size-small);
  font-weight: var(--poolit-font-weight-medium);
  transition: transform var(--poolit-transition-fast);
}

.source-link:active {
  transform: scale(0.95);
}

@media (max-width: 480px) {
  .post-header {
    padding: var(--poolit-spacing-sm);
  }

  .avatar-wrapper,
  .avatar-placeholder {
    width: 36px;
    height: 36px;
  }

  .avatar-placeholder {
    font-size: 0.875rem;
  }
}
</style>
